import { localizedAccountDisplayName, uniqueDisplayAccount } from "../utils/localizedAccountDisplayName";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
// src/pages/ExpensesDetail.tsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import {
  EyeIcon,
  ArrowDownTrayIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";

import { apiDelete, apiGet, apiGetBlob, apiPost, apiPut } from "../api/client";
import { canonicalVendorPayment, classifyVendorPaymentFailure } from "../utils/vendorPaymentIdempotency";
import { useVendorPaymentRecovery } from "../utils/vendorPaymentPersistence";
import { Button } from "../components/Button";
import { AppleComboBox } from "../components/AppleComboBox";
import { AppleCard } from "../components/AppleCard";
import { PurchaseInfoHeader } from "../components/PurchaseInfoHeader";
import MemoSection from "../components/MemoSection";
import { companyRoute } from "../utils/routeHelpers";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import { ymdTodayLocal, formatAnyDateToLocale } from "../utils/dateUtils";
import { A } from "../styles/appleClasses";
import { AppleDateInput } from "../components/AppleDateInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import {
  extractApiDetail,
  isClosedPeriodError,
} from "../utils/closedPeriod";

/* =========================
   Types
   ========================= */
type BubbleType = "paid" | "partial" | "unpaid" | "voided";
type BubbleInfo = { label: string; bubbleType: BubbleType };

interface Vendor {
  id: number;
  name: string;
  email?: string | null;
}

interface Account {
  id: number;
  name: string;
  type?: string;
  kind?: string;
  is_system?: boolean;
}

interface ExpenseLine {
  id: number;
  detail: string;
  amount: number;
}

interface ExpenseSourceDocument {
  id: number;
  original_filename?: string | null;
  content_type?: string | null;
  byte_size?: number | null;
  created_at: string;
}

interface Expense {
  id: number;
  date: string;
  company_id: number;

  vendor_id?: number | null;
  vendor?: Vendor | null;

  account_id: number;
  account?: Account | null;

  amount: number;
  status?: string | null;
  voided?: boolean;

  description?: string | null;
  source_document_id?: number | null;
  source_document?: ExpenseSourceDocument | null;
  sourceDocument?: ExpenseSourceDocument | null;
  sourceDocumentId?: number | null;

  // pay-now
  payment_account_id?: number | null;
  payment_account?: Account | null;

  // on-account
  bill_id?: number | null;
  due_date?: string | null;

  lines: ExpenseLine[];

  edited?: boolean;
  last_edited_at?: string | null;
}

interface VendorPayment {
  id: number;
  bill_id: number;
  vendor_payment_date: string;
  amount: number;
  payment_account_id: number;
  company_id: number;
}

/* =========================
   Helpers
   ========================= */
function lower(v: unknown) {
  return String(v ?? "")
    .toLowerCase()
    .trim();
}

function money(n: number | undefined | null) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return formatMoney(n);
}

function normalizeSourceDocument(
  expense: Expense | null,
): ExpenseSourceDocument | null {
  if (!expense) return null;

  const sourceDocument =
    expense.source_document || expense.sourceDocument || null;
  if (sourceDocument?.id) return sourceDocument;

  const id = expense.source_document_id || expense.sourceDocumentId || null;
  if (!id) return null;

  return {
    id,
    original_filename: null,
    content_type: null,
    byte_size: null,
    created_at: "",
  };
}

/* =========================
   Component
   ========================= */
type ExpenseDetailMessage = string | { key: ParseKeys };
const localMessage = (key: ParseKeys): ExpenseDetailMessage => ({ key });

export default function ExpensesDetail() {
  const { t } = useTranslation();
  const displayMessage = (message: ExpenseDetailMessage): string =>
    typeof message === "string" ? message : t(message.key, { date: t("sales.errors.closedDate") });
  // The shared header asks for Purchase status keys. Expense owns its badges.
  const headerLabels = (key: ParseKeys): string => {
    switch (key) {
      case "purchases.status.paid": return t("expenses.status.paid");
      case "purchases.status.partial": return t("expenses.status.partial");
      case "purchases.status.unpaid": return t("expenses.status.unpaid");
      case "purchases.status.voided": return t("expenses.status.voided");
      default: return t(key);
    }
  };
  const navigate = useNavigate();
  const { id, company_id } = useParams<{ id: string; company_id: string }>();

  const [expense, setExpense] = useState<Expense | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [vendorPayments, setVendorPayments] = useState<VendorPayment[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ExpenseDetailMessage | null>(null);

  // Add Payment
  const [paymentDate, setPaymentDate] = useState<string>(() => ymdTodayLocal());
  const [paymentAccountId, setPaymentAccountId] = useState<number | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [paymentError, setPaymentError] = useState<ExpenseDetailMessage | null>(null);

  const [paymentDocumentRoute, setPaymentDocumentRoute] = useState<string | null>(null);
  const routeIdentity = `${company_id}:${id}`;
  const routeIdentityRef = useRef(routeIdentity);
  routeIdentityRef.current = routeIdentity;
  useEffect(() => { setPaymentError(null); }, [routeIdentity]);
  const recovery = useVendorPaymentRecovery(
    company_id && paymentDocumentRoute === routeIdentity && expense && String(expense.id) === id ? Number(company_id) : null,
    paymentDocumentRoute === routeIdentity && expense && String(expense.id) === id && expense?.bill_id ? Number(expense?.bill_id) : null,
    !!expense?.voided,
    body => {
      setPaymentDate(body.vendor_payment_date);
      setPaymentAmount(body.amount);
      setPaymentAccountId(body.payment_account_id);
    },
  );
  const paying = recovery.pending;
  const paymentBody = () => {
    const saved = recovery.ticket?.submittedPayload;
    const amount = Number(paymentAmount);
    if (saved && saved.vendor_payment_date === paymentDate &&
        saved.payment_account_id === paymentAccountId &&
        (saved.amount === paymentAmount || Number(saved.amount) === amount)) return saved;
    return { company_id: Number(company_id),
      ...canonicalVendorPayment(Number(expense?.bill_id), paymentDate, amount, paymentAccountId ?? 0) };
  };
  const editedPayment = !!recovery.ticket && !recovery.exact(paymentBody());
  const recoveryMessage = recovery.problem === "corrupt"
    ? localMessage("vendorPayments.retry.corrupt")
    : recovery.problem === "storage" ? localMessage("vendorPayments.retry.cantSave")
    : recovery.ticket && !paying ? localMessage("vendorPayments.retry.uncertain") : null;


  // Memo edit
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState<string>("");
  const [descSaving, setDescSaving] = useState(false);
  const [descError, setDescError] = useState<ExpenseDetailMessage | null>(null);
  const [sourceDocumentError, setSourceDocumentError] = useState<ExpenseDetailMessage | null>(
    null,
  );
  const [sourceDocumentPreview, setSourceDocumentPreview] = useState<{
    url: string;
    contentType: string;
  } | null>(null);

  // Delete confirm modal (SaleDetail-style)
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<ExpenseDetailMessage | null>(null);

  const qCompany = company_id
    ? `?company_id=${company_id}&_=${Date.now()}`
    : `?_=${Date.now()}`;

  const paymentAccounts = useMemo(() => {
    return accounts.filter(
      (a) =>
        !a.is_system &&
        lower(a.type) === "asset" &&
        (lower(a.kind) === "cash" || lower(a.kind) === "bank"),
    );
  }, [accounts]);

  const isOnAccount = useMemo(() => {
    if (!expense) return false;
    if (expense.bill_id) return true;
    return !expense.payment_account && !expense.payment_account_id;
  }, [expense]);

  const totalPaid = useMemo(() => {
    return vendorPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  }, [vendorPayments]);

  const outstanding = useMemo(() => {
    if (!expense) return 0;
    if (!isOnAccount) return 0;
    return Math.max(0, (Number(expense.amount) || 0) - totalPaid);
  }, [expense, isOnAccount, totalPaid]);

  const isPaid = useMemo(() => {
    if (!expense) return false;
    if (expense.voided) return false;

    // pay-now = always paid
    if (!isOnAccount) return true;

    const st = lower(expense.status);
    if (st === "paid" || st === "closed") return true;

    return Math.abs((Number(expense.amount) || 0) - totalPaid) < 0.005;
  }, [expense, isOnAccount, totalPaid]);

  const bubbles: BubbleInfo[] = useMemo(() => {
    if (!expense) return [];
    if (expense.voided) return [{ label: "Voided", bubbleType: "voided" }];

    if (isPaid) return [{ label: "Paid", bubbleType: "paid" }];
    if (isOnAccount && totalPaid > 0)
      return [{ label: "Partial", bubbleType: "partial" }];
    return [{ label: "Unpaid", bubbleType: "unpaid" }];
  }, [expense, isPaid, isOnAccount, totalPaid]);

  const paymentRows = useMemo(() => {
    if (!expense) return [];
    if (isOnAccount) return vendorPayments;

    // pay-now synthetic row
    return [
      {
        id: -1,
        bill_id: 0,
        vendor_payment_date: expense.date,
        amount: Number(expense.amount) || 0,
        payment_account_id: expense.payment_account_id || 0,
        company_id: expense.company_id,
      } as VendorPayment,
    ];
  }, [expense, isOnAccount, vendorPayments]);

  const headerDueDate = useMemo(() => {
    if (!expense?.due_date) return undefined;
    return expense.due_date as any;
  }, [expense]);

  const lines = useMemo(() => {
    return Array.isArray(expense?.lines) ? expense!.lines : [];
  }, [expense]);

  const sourceDocument = useMemo(
    () => normalizeSourceDocument(expense),
    [expense],
  );

  useEffect(() => {
    return () => {
      if (sourceDocumentPreview?.url) {
        URL.revokeObjectURL(sourceDocumentPreview.url);
      }
    };
  }, [sourceDocumentPreview]);

  useEffect(() => {
    if (!sourceDocumentPreview) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSourceDocumentPreview(null);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [sourceDocumentPreview]);

  const getCategoryName = (accountId: number) => {
    return accounts.find((a) => a.id === accountId)?.name || "—";
  };

  const loadExpenseAndSupport = async () => {
    const route = `${company_id}:${id}`;
    setLoading(true);
    setError(null);

    try {
      if (!id) throw new Error("Missing id");

      const exp = await apiGet<Expense>(`/expenses/${id}${qCompany}`);
      if (routeIdentityRef.current !== route) return;
      setPaymentDocumentRoute(route);
      setExpense(exp);
      setDescDraft(exp.description || "");

      const accs = await apiGet<Account[]>(`/accounts${qCompany}`);
      if (routeIdentityRef.current !== route) return;
      setAccounts(accs || []);

      if (company_id && exp.bill_id) {
        const all = await apiGet<VendorPayment[]>(
          `/ar-ap/vendor-payments?company_id=${company_id}&_=${Date.now()}`,
        ).catch(() => []);

        if (routeIdentityRef.current !== route) return;
        const mine = (all || []).filter(
          (p) => String(p.bill_id) === String(exp.bill_id),
        );
        setVendorPayments(mine);

        // The default effect below waits for loading and preserves restored details.
      } else {
        setVendorPayments([]);
        setPaymentAmount("");
      }
    } catch {
      if (routeIdentityRef.current === route) setError(localMessage("expenses.errors.load"));
    } finally {
      if (routeIdentityRef.current === route) setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenseAndSupport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, company_id]);


  useEffect(() => {
    if (!loading && !isPaid && !expense?.voided && !recovery.ticket && !recovery.problem) {
      setPaymentAmount(outstanding > 0 ? String(outstanding) : "");
    }
  }, [loading, isPaid, expense?.voided, outstanding, recovery.ticket, recovery.problem]);

  // Close delete modal on Escape
  useEffect(() => {
    if (!showDelete) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !deleting) {
        setShowDelete(false);
        setDeleteError(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showDelete, deleting]);

  const handleAddPayment = async () => {
    if (paymentDocumentRoute !== routeIdentity || !expense || expense.voided || String(expense.id) !== id || !company_id || !expense?.bill_id || paying) return;
    setPaymentError(null);
    if (recovery.problem === "corrupt") return;
    const body = paymentBody();
    if (recovery.ticket && !recovery.exact(body)) {
      setPaymentError(localMessage("vendorPayments.retry.uncertain"));
      return;
    }
    if (!recovery.exact(body)) {
      const amount = Number(body.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        setPaymentError(localMessage("expenses.errors.validAmount"));
        return;
      }
      if (amount > outstanding + 0.005) {
        setPaymentError(localMessage("expenses.errors.overpayment"));
        return;
      }
      if (!paymentAccountId) {
        setPaymentError(localMessage("expenses.errors.selectPaymentAccountRequired"));
        return;
      }
      if (!paymentDate) { setPaymentError(localMessage("expenses.errors.paymentDate")); return; }
    }
    const operation = recovery.begin(body);
    if (!operation) return;
    try {
      await apiPost("/ar-ap/vendor-payments", {
        ...operation.attempt.submittedPayload,
        idempotency_key: operation.attempt.idempotencyKey,
      });
    } catch (error: any) {
      const kind = classifyVendorPaymentFailure(error, isClosedPeriodError);
      if (kind === "ambiguous") operation.uncertain();
      else operation.finish(); // Definite result clears the captured slot before showing it.
      if (!operation.isCurrent()) return;
      if (kind === "closed-period") setPaymentError(localMessage("sales.errors.closedSubmit"));
      else if (kind === "idempotency-conflict") setPaymentError(localMessage("vendorPayments.retry.conflict"));
      else if (kind === "voided") setPaymentError(localMessage("vendorPayments.retry.voided"));
      else if (kind === "unavailable") setPaymentError(localMessage("vendorPayments.retry.unavailable"));
      else if (kind === "ambiguous") setPaymentError(localMessage("vendorPayments.retry.uncertain"));
      else setPaymentError(extractApiDetail(error) || localMessage("purchases.errors.recordPayment"));
      return;
    }
    operation.finish(true); // Terminal ticket, but keep repeat-tap protection through refresh.
    if (!operation.isCurrent()) { operation.release(); return; }
    setPaymentAmount("");
    try { await loadExpenseAndSupport(); }
    catch { if (operation.isCurrent()) setPaymentError(localMessage("expenses.errors.load")); }
    finally { operation.release(); }
  };

  const handleSaveDescription = async () => {
    if (!expense) return;
    setDescSaving(true);
    setDescError(null);
    try {
      await apiPut(`/expenses/${expense.id}${qCompany}`, {
        description: descDraft,
      });
      setExpense({ ...expense, description: descDraft });
      setEditingDesc(false);
    } catch {
      setDescError(localMessage("expenses.errors.memo"));
    } finally {
      setDescSaving(false);
    }
  };

  const openDelete = () => {
    setDeleteError(null);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!expense) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      await apiDelete(`/expenses/${expense.id}${qCompany}`);
      navigate(companyRoute(company_id!, "/recognition/expenses"));
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setDeleteError(localMessage("sales.errors.closedEdit"));
        return;
      }

      setDeleteError(extractApiDetail(e) || localMessage("expenses.errors.void"));
    } finally {
      setDeleting(false);
    }
  };

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD";

  // Desktop scanned-invoice action buttons -- mirrors SaleInfoHeader's
  // InvoiceActionGroup `miniBtn` styling exactly (same as Purchase Detail).
  const scannedInvoiceBtn =
    "inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white " +
    "px-2.5 py-1.5 text-xs font-medium leading-none text-gray-700 " +
    "hover:bg-gray-50 active:bg-gray-100 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  // Mobile scanned-invoice icon buttons -- Sale Detail's exact `miniBtn`
  // pill dimensions (px-2.5 py-1.5, no fixed height/width), icon-only, with
  // the same w-3.5 h-3.5 icon scale.
  const scannedInvoiceMobileBtn =
    "inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 " +
    "hover:bg-gray-50 active:bg-gray-100 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  const openSourceDocument = async () => {
    if (!sourceDocument?.id) return;

    setSourceDocumentError(null);
    try {
      const blob = await apiGetBlob(
        `/source-documents/${sourceDocument.id}/preview`,
      );
      const url = URL.createObjectURL(blob);
      setSourceDocumentPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return {
          url,
          contentType:
            blob.type || sourceDocument.content_type || "application/octet-stream",
        };
      });
    } catch {
      setSourceDocumentError(localMessage("purchases.errors.openDocument"));
    }
  };

  const downloadSourceDocument = async () => {
    if (!sourceDocument?.id) return;

    setSourceDocumentError(null);
    try {
      const blob = await apiGetBlob(
        `/source-documents/${sourceDocument.id}/download`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        sourceDocument.original_filename || `source-document-${sourceDocument.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setSourceDocumentError(localMessage("purchases.errors.downloadDocument"));
    }
  };

  const closeSourceDocumentPreview = () => {
    setSourceDocumentPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  /* =========================
     Render
     ========================= */
  if (loading) {
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center ${A.body} text-gray-500`}
      >
        {t("purchases.edit.loading")}
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div
        className={`min-h-screen bg-gray-50 flex items-center justify-center ${A.body} text-red-600 px-4`}
      >
        {error ? displayMessage(error) : t("expenses.errors.notFound")}
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-6`}
    >
      {/* Delete Modal (SaleDetail-style) */}
      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
          onClick={() => {
            if (deleting) return;
            setShowDelete(false);
            setDeleteError(null);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className={`${A.modal} w-full max-w-sm p-4 shadow-md`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={A.title}>{t("expenses.detail.voidTitle")}</div>
            <div className={`${A.body} text-gray-600 mt-1`}>
              {t("expenses.detail.voidMessage")}
            </div>

            {deleteError && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center text-[13px] text-red-700 whitespace-pre-wrap">
                {displayMessage(deleteError)}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  if (deleting) return;
                  setShowDelete(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
              >
                {t("directory.common.cancel")}
              </Button>

              <Button
                variant="danger"
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? t("expenses.detail.voiding") : t("expenses.detail.void")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky header */}
      {sourceDocumentPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/40 px-3 py-4 sm:px-6"
          role="dialog"
          aria-modal="true"
          aria-label={t("purchases.detail.preview")}
        >
          <div className="mx-auto flex h-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
              <div className={`${A.label} font-medium text-gray-600`}>
                {t("purchases.detail.scannedInvoice")}
              </div>
              <button
                type="button"
                onClick={closeSourceDocumentPreview}
                className={`${iconBtn} group`}
                aria-label={t("purchases.detail.closePreview")}
                title={t("directory.common.close")}
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition" />
              </button>
            </div>

            <div className="min-h-0 flex-1 bg-gray-50">
              {sourceDocumentPreview.contentType
                .toLowerCase()
                .startsWith("image/") ? (
                <div className="flex h-full items-center justify-center overflow-auto p-3">
                  <img
                    src={sourceDocumentPreview.url}
                    alt={t("purchases.detail.scannedInvoice")}
                    className="max-h-full max-w-full rounded-xl object-contain"
                  />
                </div>
              ) : sourceDocumentPreview.contentType.toLowerCase() ===
                "application/pdf" ? (
                <object
                  data={sourceDocumentPreview.url}
                  type="application/pdf"
                  className="h-full w-full"
                >
                  <div className="flex h-full items-center justify-center p-4 text-center text-xs text-gray-500">
                    {t("purchases.detail.pdfUnavailable")}
                  </div>
                </object>
              ) : (
                <div className="flex h-full items-center justify-center p-4 text-center text-xs text-gray-500">
                  {t("purchases.detail.previewUnavailable")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-6xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <div className="flex-none">
          <button
            type="button"
            onClick={() =>
              navigate(companyRoute(company_id!, "/recognition/expenses"))
            }
            className={`${iconBtn} group`}
            aria-label={t("directory.common.back")}
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 group-hover:text-gray-600 transition" />
          </button>
        </div>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className={`${A.title} tracking-tight text-center`}>
            {t("expenses.detail.title")}
          </h2>
        </div>

        <div className="flex-none ml-auto">
          <button
            type="button"
            onClick={() => navigate(companyRoute(company_id!, "/"))}
            className={`${iconBtn} group`}
            aria-label={t("directory.common.close")}
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 group-hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      {/* Main card */}
      <div className={`${A.card} w-full max-w-3xl mx-auto px-6 py-5`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <PurchaseInfoHeader
              purchaseLabels={headerLabels}
              date={expense.date}
              vendor={expense.vendor?.name}
              email={expense.vendor?.email || undefined}
              statusBubbles={bubbles}
              outstanding={isOnAccount && !isPaid ? outstanding : 0}
              dueDate={headerDueDate as any}
              total={expense.amount}
              isPaid={!!isPaid}
              edited={!!expense.edited && !expense.voided}
              lastEditedAt={expense.last_edited_at || undefined}
              statusRowEndSlot={
                sourceDocument ? (
                  <span className="inline-flex items-center gap-2 sm:hidden">
                    <button
                      type="button"
                      className={scannedInvoiceMobileBtn}
                      aria-label={t("purchases.detail.viewScan")}
                      title={t("purchases.detail.viewScan")}
                      onClick={openSourceDocument}
                    >
                      <PaperClipIcon
                        className="w-3.5 h-3.5 text-gray-400"
                        aria-hidden="true"
                      />
                    </button>

                    <button
                      type="button"
                      className={scannedInvoiceMobileBtn}
                      aria-label={t("purchases.detail.downloadScan")}
                      title={t("purchases.detail.downloadScan")}
                      onClick={downloadSourceDocument}
                    >
                      <ArrowDownTrayIcon
                        className="w-3.5 h-3.5 text-gray-400"
                        aria-hidden="true"
                      />
                    </button>
                  </span>
                ) : null
              }
            />
          </div>

          {/* Scanned invoice actions (desktop) -- matches Sale Detail's /
              Purchase Detail's labelled top-right invoice action group. */}
          {sourceDocument && (
            <div className="hidden shrink-0 sm:flex sm:items-center sm:gap-2 sm:self-start sm:ml-4">
              <button
                type="button"
                onClick={openSourceDocument}
                className={scannedInvoiceBtn}
                aria-label={t("purchases.detail.viewScan")}
                title={t("purchases.detail.viewScan")}
              >
                <EyeIcon className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                <span>{t("purchases.detail.view")}</span>
              </button>

              <button
                type="button"
                onClick={downloadSourceDocument}
                className={scannedInvoiceBtn}
                aria-label={t("purchases.detail.downloadScan")}
                title={t("purchases.detail.downloadScan")}
              >
                <ArrowDownTrayIcon
                  className="w-3.5 h-3.5 text-gray-400"
                  aria-hidden="true"
                />
                <span>{t("purchases.detail.download")}</span>
              </button>
            </div>
          )}
        </div>

        {sourceDocumentError && (
          <div className="mb-4 text-xs font-medium text-red-600">
            {displayMessage(sourceDocumentError)}
          </div>
        )}

        {/* Lines (Desktop) */}
        <div className="hidden sm:block mb-5">
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <table className={`w-full table-fixed ${A.body}`}>
              <colgroup>
                <col style={{ width: "40%" }} />
                <col style={{ width: "40%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>

              <thead className="bg-gray-50">
                <tr>
                  <th
                    className={`text-left px-3 py-2 ${A.label} font-medium text-gray-500`}
                  >
                    {t("expenses.common.category")}
                  </th>
                  <th
                    className={`text-left px-3 py-2 ${A.label} font-medium text-gray-500`}
                  >
                    {t("expenses.common.detail")}
                  </th>
                  <th
                    className={`text-right px-3 py-2 ${A.label} font-medium text-gray-500 ${A.num}`}
                  >
                    {t("sales.common.amount")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <div
                        className="whitespace-normal break-words"
                        style={
                          {
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          } as any
                        }
                      >
                        {localizedAccountDisplayName(uniqueDisplayAccount(accounts, expense.account_id), t, getCategoryName(expense.account_id))}
                      </div>
                    </td>

                    <td className="px-3 py-2">{line.detail || "—"}</td>

                    <td
                      className={`px-3 py-2 text-right font-medium whitespace-nowrap ${A.num}`}
                    >
                      {money(line.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lines (Mobile) */}
        <div className="block sm:hidden space-y-3 mb-5">
          {lines.map((line) => (
            <div key={line.id} className={`${A.card} px-4 py-3`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className={`${A.label} font-medium mb-1`}>{t("expenses.common.category")}</div>
                  <div className="break-words">
                    {localizedAccountDisplayName(uniqueDisplayAccount(accounts, expense.account_id), t, getCategoryName(expense.account_id))}
                  </div>
                </div>

                <div
                  className={`text-right ${A.num} font-medium whitespace-nowrap`}
                >
                  {money(line.amount)}
                </div>
              </div>

              <div className="mt-2">
                <div className={`${A.label} font-medium mb-1`}>{t("expenses.common.detail")}</div>
                <div className="break-words">{line.detail || "—"}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className={`mb-4 text-right ${A.num}`}>
          <span className={A.title}>{t("sales.common.totalWithColon")} {money(expense.amount)}</span>
        </div>

        {/* Payment History */}
        {paymentRows.length > 0 && (
          <div className="mb-6">
            <div className={`${A.label} text-gray-500 mb-2 font-medium`}>
              {t("purchases.detail.paymentHistory")}
            </div>

            {/* Desktop */}
            <div className="hidden sm:block">
              <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden mb-2">
                <table className={`w-full table-fixed ${A.body}`}>
                  <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>

                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className={`text-left px-3 py-2 ${A.label} font-medium text-gray-500`}
                      >
                        {t("sales.common.date")}
                      </th>
                      <th
                        className={`text-left px-3 py-2 ${A.label} font-medium text-gray-500`}
                      >
                        {t("expenses.detail.paymentAccount")}
                      </th>
                      <th
                        className={`text-right px-3 py-2 ${A.label} font-medium text-gray-500 ${A.num}`}
                      >
                        {t("sales.common.amount")}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paymentRows.map((p, i) => {
                      const acct = accounts.find(
                        (a) => a.id === (p as any).payment_account_id,
                      );
                      const amountVal =
                        typeof (p as any).amount === "number"
                          ? (p as any).amount
                          : Number((p as any).amount || 0);

                      return (
                        <tr
                          key={(p as any).id || i}
                          className="border-t border-gray-100"
                        >
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatAnyDateToLocale(
                              (p as any).vendor_payment_date,
                            )}
                          </td>

                          <td className="px-3 py-2">
                            <div
                              className="whitespace-normal break-words"
                              style={
                                {
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                } as any
                              }
                            >
                              {acct
                                ? acct.name
                                : expense.payment_account?.name || "—"}
                            </div>
                          </td>

                          <td
                            className={`px-3 py-2 text-right font-medium whitespace-nowrap ${A.num}`}
                          >
                            {money(amountVal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile */}
            <div className="block sm:hidden space-y-2">
              {paymentRows.map((p, i) => {
                const acct = accounts.find(
                  (a) => a.id === (p as any).payment_account_id,
                );
                const amountVal =
                  typeof (p as any).amount === "number"
                    ? (p as any).amount
                    : Number((p as any).amount || 0);

                return (
                  <div
                    key={(p as any).id || i}
                    className={`${A.card} px-4 py-3`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className={`${A.label} font-medium mb-0.5`}>
                          {t("sales.common.date")}
                        </div>
                        <div>
                          {formatAnyDateToLocale(
                            (p as any).vendor_payment_date,
                          )}
                        </div>
                      </div>

                      <div className={`text-right ${A.num}`}>
                        <div className={`${A.label} font-medium mb-0.5`}>
                          {t("sales.common.amount")}
                        </div>
                        <div className="font-medium">{money(amountVal)}</div>
                      </div>
                    </div>

                    <div>
                      <div className={`${A.label} font-medium mb-0.5`}>
                        {t("expenses.detail.paymentAccount")}
                      </div>
                      <div
                        className="whitespace-normal break-words"
                        style={
                          {
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          } as any
                        }
                      >
                        {acct
                          ? acct.name
                          : expense.payment_account?.name || "—"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={`text-right pr-1 ${A.num} mt-2`}>
              <span className={`${A.label} text-gray-500 font-medium`}>
                {t("expenses.detail.paidAmount")}
              </span>
              <span className={`ml-2 ${A.body} font-medium text-gray-700`}>
                {money(isOnAccount ? totalPaid : expense.amount)}
              </span>
            </div>
          </div>
        )}

        {/* Add Payment */}
        {isOnAccount && (!isPaid || recovery.ticket || recovery.problem) && !expense.voided && (
          <div className="bg-gray-50 rounded-2xl px-4 py-3 shadow-sm mb-6 ring-1 ring-black/5">
            <div className={`${A.label} text-gray-500 mb-2 font-medium`}>
              {t("purchases.detail.addPayment")}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={`${A.label} font-medium mb-1`}>
                  {t("sales.common.paymentDate")}
                </label>
                <AppleDateInput
                  openCalendarLabel={t("sales.shared.openCalendar")}
                  valueYmd={paymentDate}
                  onChangeYmd={setPaymentDate}
                  ariaLabel={t("sales.common.paymentDate")}
                  className={A.num}
                />
              </div>

              <div>
                <label className={`${A.label} font-medium mb-1`}>{t("expenses.detail.paymentAccount")}</label>
                <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                  options={paymentAccounts}
                  value={
                    paymentAccounts.find(
                      (acc) => acc.id === paymentAccountId,
                    ) || null
                  }
                  onChange={(acc: Account | null) =>
                    setPaymentAccountId(acc ? acc.id : null)
                  }
                  getOptionLabel={(acc: Account) => acc.name}
                  placeholder={t("expenses.detail.selectPaymentAccount")}
                />
              </div>

              <div>
                <label className={`${A.label} font-medium mb-1`}>{t("sales.common.amount")}</label>
                <AppleNumberInput
                  min={0.01}
                  max={recovery.ticket ? undefined : outstanding}
                  step={0.01}
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className={`${A.num} text-right`}
                  placeholder={money(outstanding)}
                  autoComplete="off"
                />
              </div>
            </div>

            {recovery.problem === "corrupt" && (
              <Button onClick={() => { recovery.startNew(); setPaymentError(null); }}>
                {t("vendorPayments.retry.startNew")}
              </Button>
            )}
            {editedPayment && !recovery.problem && (
              <Button onClick={() => { recovery.restore(); setPaymentError(null); }} disabled={paying}>
                {t("vendorPayments.retry.restore")}
              </Button>
            )}
            <div className="flex justify-end mt-4">
              <Button
                onClick={handleAddPayment}
                disabled={paying || !paymentAccountId || recovery.problem === "corrupt"}
                variant="primary"
              >
                {paying ? t("directory.common.savingDots") : recovery.ticket ? t("vendorPayments.retry.retry") : t("purchases.detail.addPayment")}
              </Button>
            </div>

            <div className="min-h-[18px] mt-2">
              {(recoveryMessage || paymentError) ? (
                <div className="text-xs text-red-600 font-medium">
                  {displayMessage((recoveryMessage || paymentError)!)}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Memo */}
        <div className="mb-6">
          {/* Memo */}
          {!editingDesc ? (
            <MemoSection
              editAriaLabel={t("sales.common.editMemo")}
              label={t("sales.common.memo")}
              mode="view"
              variant="card"
              value={expense.description || ""}
              emptyText={t("purchases.detail.noMemo")}
              className="mb-6"
              onEditClick={() => {
                setEditingDesc(true);
                setDescDraft(expense.description || "");
                setDescError(null);
              }}
            />
          ) : (
            <AppleCard padding="p-4" className="mb-6">
              <MemoSection
                editAriaLabel={t("sales.common.editMemo")}
                mode="edit"
                variant="plain"
                label={t("sales.common.memo")}
                value={descDraft}
                onChange={setDescDraft}
                rows={2}
                disabled={descSaving}
                placeholder={t("sales.common.memoPlaceholder")}
              />

              <div className="flex gap-3 justify-end mt-3">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    setEditingDesc(false);
                    setDescDraft(expense.description || "");
                    setDescError(null);
                  }}
                  disabled={descSaving}
                >
                  {t("directory.common.cancel")}
                </Button>

                <Button
                  variant="primary"
                  type="button"
                  onClick={handleSaveDescription}
                  disabled={descSaving}
                >
                  {descSaving ? t("directory.common.savingDots") : t("directory.common.save")}
                </Button>
              </div>

              <div className="min-h-[18px] mt-2">
                {descError ? (
                  <div className="text-xs text-red-600 font-medium">
                    {displayMessage(descError)}
                  </div>
                ) : null}
              </div>
            </AppleCard>
          )}
        </div>

        {/* Actions (page delete stays grey) */}
        <div className="flex justify-end">
          <Button variant="secondary" type="button" onClick={openDelete}>
            {t("expenses.detail.void")}
          </Button>
        </div>
      </div>
    </div>
  );
}
